import { useSubscribe } from 'hooks/useSubscribe';

import { MessagingServiceEvent } from './messaging-service-plugin';
import {
  useMessagingServiceStatusValue,
  useSetMessagingServiceStatus,
} from 'state/messagingServiceStatus';
import { AuthEvent } from './auth-plugin';
import { useSetGenesysError } from './error/error-state';

const Status = {
  Loading: 'Loading',
  StartingConversation: 'StartingConversation',
  InConversation: 'InConversation',
  EndingConversation: 'EndingConversation',
  ConversationClearedLoggedOut: 'ConversationClearedLoggedOut',
};

const INVALID_STATUS_CODE = 403;
const INVALID_STATUS = 'Invalid token';

export const useMessagingServiceStatus = () => {
  const status = useMessagingServiceStatusValue();
  const setStatus = useSetMessagingServiceStatus();
  const setGenesysError = useSetGenesysError();

  const isStartingConversation = status === Status.StartingConversation;
  const isEndingConversation = status === Status.EndingConversation;

  const setConversationStarting = () => setStatus(Status.StartingConversation);

  const setConversationEnding = () => setStatus(Status.EndingConversation);

  const setConversationStarted = () => setStatus(Status.InConversation);

  const setResetConversationLoading = () => setStatus('');

  useSubscribe(
    MessagingServiceEvent.MessagesReceived,
    (topic, { messages }) => {
      const [currentMessage] = messages;
      const { direction } = currentMessage;

      if (direction.toLowerCase() === 'outbound') {
        setConversationStarted();
        setGenesysError('');
      }
    },
    isStartingConversation,
  );

  useSubscribe(MessagingServiceEvent.Restored, () => {
    setConversationStarted();
  });

  useSubscribe(AuthEvent.LoggedOut, () => {
    setStatus(Status.ConversationClearedLoggedOut);
  });

  // reset loading on all error scenarios
  useSubscribe(MessagingServiceEvent.RestoreFailed, () => {
    setStatus('');
  });

  useSubscribe(
    AuthEvent.Error,
    (topic, { status: statusCode, message } = {}) => {
      if (statusCode !== INVALID_STATUS_CODE && message !== INVALID_STATUS) {
        setStatus('');
      }
    },
  );

  useSubscribe(MessagingServiceEvent.Error, () => {
    setStatus('');
  });

  return {
    isStartingConversation,
    isEndingConversation,
    setConversationStarting,
    setConversationEnding,
    setResetConversationLoading,
  };
};
